import { useCallback, useEffect, useRef, useState } from 'react';

const pickMime = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const m of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
};

// Records one spoken clip from the microphone. onClip(blob, seconds) runs when it stops.
export default function useMicRecorder({ maxSeconds = 90, onClip }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunks = useRef([]);
  const startedAt = useRef(0);
  const timer = useRef(null);
  const onClipRef = useRef(onClip);
  onClipRef.current = onClip;

  const supported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';

  const cleanup = useCallback(() => {
    clearInterval(timer.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const stop = useCallback(() => {
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
  }, []);

  const start = useCallback(async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data); };
      rec.onstop = () => {
        const secs = (Date.now() - startedAt.current) / 1000;
        const blob = new Blob(chunks.current, { type: rec.mimeType || mime || 'audio/webm' });
        cleanup();
        setRecording(false);
        if (blob.size > 0) onClipRef.current?.(blob, secs);
      };
      recRef.current = rec;
      startedAt.current = Date.now();
      setSeconds(0);
      rec.start();
      setRecording(true);
      timer.current = setInterval(() => {
        const s = Math.floor((Date.now() - startedAt.current) / 1000);
        setSeconds(s);
        if (s >= maxSeconds) stop();
      }, 500);
    } catch {
      cleanup();
      setRecording(false);
      setError('We could not use the microphone. Ask a grown-up to allow it in the browser.');
    }
  }, [cleanup, maxSeconds, stop]);

  useEffect(() => () => { cleanup(); }, [cleanup]);

  return { supported, recording, seconds, error, start, stop };
}
